import { useEffect } from "react";

/**
 * Hold a screen wake lock while `active` (a take is in progress — recording OR paused).
 * Android's default screen timeout (30s–2min) is shorter than the recording cap, and a
 * sleeping screen kills the camera/display track mid-take. Best-effort: browsers without
 * the API (or a denied request, e.g. battery saver) change nothing — the capture screens'
 * track-reacquire paths still handle a dead track.
 *
 * The OS auto-releases the lock when the tab is hidden; we re-acquire on return to the
 * foreground (visibilitychange) so a brief app switch doesn't leave the screen sleepable.
 */
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;
    let lock: WakeLockSentinel | null = null;
    let disposed = false;
    const acquire = async () => {
      try {
        const l = await navigator.wakeLock.request("screen");
        if (disposed) {
          void l.release().catch(() => {});
          return;
        }
        lock = l;
      } catch {
        /* denied — screen may sleep; nothing else to do */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
    };
    void acquire();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibility);
      void lock?.release().catch(() => {});
      lock = null;
    };
  }, [active]);
}
